import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  MANUAL_REVIEW_QUEUE_QUERY_KEY,
  manualReviewApi,
  type ManualReviewQueueItemDto,
} from '../../api/admin';
import { adminConsoleAuthApi, type AdminConsoleUser } from '../../api/adminConsole';
import { ADMIN_CONSOLE_ME_QUERY_KEY, isOrchestratorDisabled } from '../../lib/adminSession';
import { PermissionKey } from '@c4ytechnology/authz';
import { hasAdminPermission } from '../../lib/adminPermissions';
import { shouldShowReviewQueuePanel, splitReviewQueue } from '../../lib/manualReviewAssignment';
import { ManualReviewReassignDialog } from './ManualReviewReassignDialog';
import { useTheme } from '@mui/material/styles';
import { monoLinkSx } from '../admin/adminListPageUi';

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function assigneeLabel(row: ManualReviewQueueItemDto): string {
  if (row.state !== 'ASSIGNED') return 'Waiting';
  return row.assigneeDisplayName?.trim() || row.assigneeEmail || 'Assigned';
}

export function ManualReviewQueuePanel() {
  const theme = useTheme();
  const [reassignId, setReassignId] = useState<string | null>(null);

  const live = typeof window !== 'undefined' && !isOrchestratorDisabled();

  const meQ = useQuery<AdminConsoleUser>({
    queryKey: ADMIN_CONSOLE_ME_QUERY_KEY,
    queryFn: () => adminConsoleAuthApi.me(),
    staleTime: 60_000,
    retry: false,
    enabled: live,
  });

  const queueQ = useQuery({
    queryKey: MANUAL_REVIEW_QUEUE_QUERY_KEY,
    queryFn: () => manualReviewApi.queue(),
    enabled: live,
    refetchInterval: 15_000,
    retry: false,
  });

  const canReassign = hasAdminPermission(meQ.data, PermissionKey.MANUAL_REVIEW_REASSIGN);
  // A reviewer is whoever can decide a manual-review file: the permission the decision endpoint
  // checks (orchestrator POST /admin/applications/:id/decision). Under v1 it is translated from the
  // Loan Reviewers group's actions.applications.decide, so real reviewers see the same panel;
  // under v2 group membership no longer decides anything.
  const isReviewer = hasAdminPermission(meQ.data, PermissionKey.APPLICATIONS_DECIDE);
  const items = queueQ.data?.items ?? [];
  const split = useMemo(
    () => splitReviewQueue(items, [meQ.data?.email]),
    [items, meQ.data?.email],
  );

  if (!live) return null;
  if (queueQ.isError) {
    return (
      <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
        Could not load the manual-review queue. Assignment still runs in the background.
      </Alert>
    );
  }
  if (!queueQ.data && queueQ.isPending) return null;
  if (!shouldShowReviewQueuePanel({ itemCount: items.length, hasOwnFile: !!split.mine, canReassign, isReviewer })) return null;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mb: 2 }} data-testid="manual-review-queue">
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>Manual review queue</Typography>
            <Typography variant="caption" color="text.secondary">
              One file per reviewer. Everyone can see the line; only the assignee can decide their file.
            </Typography>
          </Box>
          <Chip
            size="small"
            label={`${split.waiting.length} waiting · ${split.assigned.length + (split.mine ? 1 : 0)} assigned`}
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        {split.mine ? (
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                component={RouterLink}
                to={`/applications/${encodeURIComponent(split.mine.applicationId)}`}
                sx={{ fontWeight: 800 }}
              >
                Open
              </Button>
            }
          >
            Your file: <strong>{split.mine.applicationId}</strong>
            {split.mine.assignedAt ? ` · assigned ${formatWhen(split.mine.assignedAt)}` : ''}
          </Alert>
        ) : (
          <Typography variant="body2" color="text.secondary">
            You do not have a file right now. Clock on to receive the next one in line.
          </Typography>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Application</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Reviewer</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Queued</TableCell>
              {canReassign ? <TableCell /> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => (
              <TableRow key={row.applicationId} selected={row.assignedToCaller}>
                <TableCell>{row.queuePosition ?? '—'}</TableCell>
                <TableCell>
                  <Typography
                    component={RouterLink}
                    to={`/applications/${encodeURIComponent(row.applicationId)}`}
                    sx={monoLinkSx(theme)}
                  >
                    {row.applicationId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={row.state === 'ASSIGNED' ? 'success' : 'warning'}
                    variant="outlined"
                    label={row.state === 'ASSIGNED' ? 'Assigned' : 'Waiting'}
                    sx={{ fontSize: 11, fontWeight: 700 }}
                  />
                </TableCell>
                <TableCell>{assigneeLabel(row)}</TableCell>
                <TableCell>{formatWhen(row.queuedAt)}</TableCell>
                {canReassign ? (
                  <TableCell align="right">
                    <Button size="small" onClick={() => setReassignId(row.applicationId)} sx={{ textTransform: 'none' }}>
                      Reassign
                    </Button>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
      {reassignId ? (
        <ManualReviewReassignDialog
          applicationId={reassignId}
          open
          onClose={() => setReassignId(null)}
        />
      ) : null}
    </Paper>
  );
}
